import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import type { AgentDetail, ExecutionSummary } from '../../../types/agent-studio';
import { AgentStatusPill, PerformanceSparkline } from '../../../components/agent-studio';

export interface OverviewTabProps {
  agent: AgentDetail;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} д назад`;
}

const INVOCATION_SOURCE_RU: Record<string, string> = {
  pipeline:     'Пайплайн',
  studio_test:  'Тест студии',
  task:         'Задача',
  eval_harness: 'Оценка',
};

export function OverviewTab({ agent }: OverviewTabProps) {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState<ExecutionSummary[] | null>(null);
  const [execLoading, setExecLoading] = useState(true);
  const [execError, setExecError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchExecutions() {
      try {
        setExecLoading(true);
        setExecError(null);
        const res = await api.listAgentExecutions(agent.system_name, { limit: 5 });
        if (!cancelled) setExecutions(res.executions);
      } catch (err) {
        if (!cancelled) setExecError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setExecLoading(false);
      }
    }
    fetchExecutions();
    return () => { cancelled = true; };
  }, [agent.system_name]);

  const av = agent.active_version;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Section 1 — Active version summary */}
      {!av ? (
        <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
          <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>Нет активной версии.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: '24px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '12px' }}>АКТИВНАЯ ВЕРСИЯ</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <span className="t-display" style={{ fontSize: '28px' }}>v{av.version_number}</span>
            <AgentStatusPill status="active" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px', fontSize: '13px', color: 'var(--ink-secondary)' }}>
            <div>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-primary)' }}>
                {av.model_provider}
              </span>
              {' · '}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-primary)' }}>
                {av.model_name}
              </span>
            </div>
            <div>Создана {formatDate(av.created_at)}</div>
            <div>Температура: {av.temperature}</div>
            <div>Автор: {av.created_by}</div>
            <div>Макс. шагов: {av.max_turns ?? '—'}</div>
            <div>Навыков: {av.skills.length}</div>
            <div>Память: {av.memory_scope}</div>
            <div>Доп. параметров: {Object.keys(av.extra_config).length}</div>
          </div>
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => navigate(`/agents/${agent.system_name}/versions/${av.id}`)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--navy)',
                fontWeight: 500,
              }}
            >
              Открыть детали версии →
            </button>
          </div>
        </div>
      )}

      {/* Section 2 — Performance sparklines */}
      <div className="card" style={{ padding: '24px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '16px' }}>ПОКАЗАТЕЛИ (7 ДНЕЙ)</div>
        {agent.performance_trend.length === 0 ? (
          <p className="t-body-sm" style={{ color: 'var(--ink-tertiary)', margin: 0 }}>
            Пока недостаточно данных для графиков.
          </p>
        ) : (
          <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
            <PerformanceSparkline data={agent.performance_trend} metric="avg_latency" label="Латентность" />
            <PerformanceSparkline data={agent.performance_trend} metric="cost" label="Стоимость" />
            <PerformanceSparkline data={agent.performance_trend} metric="error_rate" label="Ошибки" />
          </div>
        )}
      </div>

      {/* Section 3 — Recent activity */}
      <div className="card" style={{ padding: '24px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '16px' }}>ПОСЛЕДНИЕ ЗАПУСКИ</div>
        {execLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="card"
                style={{
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  opacity: 0.4,
                  minHeight: '52px',
                }}
              >
                <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '80px', borderRadius: '4px' }} />
                <div style={{ background: 'var(--bg-subtle)', height: '20px', width: '60px', borderRadius: '12px' }} />
                <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '44px', borderRadius: '4px' }} />
                <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '44px', borderRadius: '4px' }} />
                <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '60px', borderRadius: '4px', marginLeft: 'auto' }} />
              </div>
            ))}
          </div>
        )}
        {!execLoading && execError && (
          <p className="t-body-sm" style={{ color: 'var(--red)', margin: 0 }}>
            Ошибка загрузки запусков: {execError}
          </p>
        )}
        {!execLoading && !execError && executions && executions.length === 0 && (
          <p className="t-body-sm" style={{ color: 'var(--ink-tertiary)', margin: 0 }}>
            Запусков пока не было.
          </p>
        )}
        {!execLoading && !execError && executions && executions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {executions.map(ex => (
              <div
                key={ex.id}
                onClick={() => navigate(`/agents/${agent.system_name}/executions/${ex.id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background 0.12s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-subtle)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', minWidth: '90px' }}>
                  {timeAgo(ex.started_at)}
                </span>
                <AgentStatusPill status={ex.status} />
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)', fontFamily: 'var(--font-mono)' }}>
                  {ex.latency_seconds != null ? `${ex.latency_seconds.toFixed(1)}с` : '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)', fontFamily: 'var(--font-mono)' }}>
                  {ex.cost_usd != null ? `$${ex.cost_usd.toFixed(4)}` : '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>
                  {INVOCATION_SOURCE_RU[ex.invocation_source] ?? ex.invocation_source}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
